import type { StudentHistoryApi } from '@mathflat/domain/@entities/StudentHistory/api'
import type { Entity as StudentWorksheetEntity } from '@mathflat/domain/@entities/StudentWorksheet/dto'
import type { DefaultResponse } from '@mathflat/shared/@types/mathflatApi'

import type { RegisterDeviceEventDetail } from '~/@common/types/FCMRegister'
import maxios from '~/@common/utils/maxios'

import type {
  AchievementByCurriculumRequestDto,
  DateRangeRequestDto,
  ParentMaterialViewRequestDto,
  RecentStudiesRequestDto,
} from './request'
import type {
  AssignedChallenge,
  DailyWorksheetDto,
  PushNotificationPermission,
  RecentChallengeByCurriculum,
  RecentChallengeDto,
  RecentStudyDto,
  RecentStudyItem,
  SmileChallengeDto,
  StudentWeeklyHistoryDto,
  WeeklyInfoDto,
} from './response'

// const delay = async (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

export const homeApi = {
  fetchWeeklyInfo: async (date?: string) => {
    const { data } = await maxios.get<DefaultResponse<WeeklyInfoDto>>('/student-home/weekly', {
      params: { date },
    })
    return data
  },

  fetchRecentStudies: async (params: RecentStudiesRequestDto) => {
    const { data } = await maxios.get<DefaultResponse<RecentStudyDto>>(
      `/student-home/materialized-view`,
      {
        params,
      },
    )
    return data
  },

  fetchRecentChallenges: async () => {
    const { data } = await maxios.get<DefaultResponse<RecentChallengeDto | null>>(
      '/student-home/recently-challenge',
    )
    return data
  },

  fetchUserOpinion: async (opinion: string) => {
    return maxios.post(`/student-home/opinion`, {
      opinion,
    })
  },

  fetchSmileChallenge: async (studentId: string, curriculumKey?: string) => {
    const { data } = await maxios.get<DefaultResponse<SmileChallengeDto>>(
      `/student-history/achievement/student/${studentId}/strengths-and-weaknesses-chips`,
      {
        params: {
          curriculumKey,
        },
      },
    )
    return data
  },

  fetchTodayLearning: async (studentId: string) => {
    const { data } = await maxios.get<DefaultResponse<DailyWorksheetDto>>(
      `/worksheet/daily/${studentId}`,
    )
    return data.dailyWorksheets
  },

  postDailyWorksheet: async (studentId: string) => {
    const { data } = await maxios.post<
      DefaultResponse<{
        studentWorksheetId: StudentWorksheetEntity.StudentWorksheet['id']
      }>
    >(`/worksheet/daily`, { studentId })
    return data.studentWorksheetId
  },

  // 푸시 알림 관련
  getNotificationPermission: async () => {
    const { pushEnabled } = await maxios.get<PushNotificationPermission>('/notifications/settings')
    return pushEnabled
  },

  patchPushNotificationPermission: async (pushEnabled: boolean) => {
    return await maxios.patch('/notifications/settings/push', { pushEnabled })
  },

  postFcmNotificationToken: async (params: RegisterDeviceEventDetail) => {
    return await maxios.post('/notifications/fcm-tokens', params)
  },

  // 선생님이 설정한 챌린지
  fetchAssignedChallenge: async () => {
    const data = await maxios.get<AssignedChallenge>('/student-home/smile-challenges')
    return data
  },

  // 최근 챌린지 커리큘럼
  fetchRecentChallengeByCurriculum: async () => {
    const data = await maxios.get<RecentChallengeByCurriculum>(
      '/student-home/recently-challenge-curriculums',
    )
    return data
  },

  // 성취도 조회 (curriculumKey 기반)
  fetchAchievementsByCurriculum: async (
    studentId: string,
    params: AchievementByCurriculumRequestDto,
  ) => {
    const { data } = await maxios.get<
      DefaultResponse<StudentHistoryApi.성취도_등급_조회.ConceptChip[]>
    >(`/student-history/achievement/student/${studentId}`, {
      params,
    })
    return data
  },

  /** 주간 학습 요약 (4주 차트 데이터) — 베이직 학생 / 학부모 공용 */
  fetchWeeklyHistories: async (studentId: string, params: DateRangeRequestDto) => {
    const { data } = await maxios.get<DefaultResponse<StudentWeeklyHistoryDto[]>>(
      `/student-history/analysis/summary/weekly/${studentId}`,
      { params },
    )
    return data
  },

  // ─── 학부모 학습홈 전용 ───

  /** 학습 카드 통합 조회 (학습지/교재/숙제/경시대회) */
  fetchParentMaterialView: async (params: ParentMaterialViewRequestDto, signal?: AbortSignal) => {
    const { data } = await maxios.get<DefaultResponse<RecentStudyItem[]>>(
      `/parent-home/materialized-view`,
      { params, signal },
    )
    return data
  },
}
